<template>
  <div v-title="$route.meta.title" class="market-location-page">
    <div v-if="paramError" class="param-error">
      <el-alert :title="paramError" type="error" show-icon :closable="false" />
    </div>
    <template v-else>
      <div class="loading-indicator" v-if="loading">
        <i class="el-icon-loading"></i>
        <span>正在加载...</span>
      </div>

      <!-- 左上搜索 -->
      <div class="search-wrap">
        <div class="search-box">
          <i class="el-icon-search search-icon"></i>
          <input
            v-model="searchKeyword"
            class="search-input"
            placeholder="搜索地址（如：世纪大道）"
            @keyup.enter="searchAddress">
          <button
            v-if="searchKeyword || searchResults.length"
            class="search-clear"
            type="button"
            title="清除"
            @click="clearSearch">
            <i class="el-icon-circle-close"></i>
          </button>
          <button class="search-btn" :disabled="searching" @click="searchAddress">
            {{ searching ? '搜索中' : '搜索' }}
          </button>
        </div>
        <div v-if="searchResults.length" class="search-results">
          <div
            v-for="item in searchResults"
            :key="item.uid"
            class="res-item"
            :class="{ 'is-active': previewUid === item.uid }"
            @click="useLocation(item, 'search')">
            <span class="res-idx">{{ item.index }}</span>
            <div class="res-main">
              <div class="res-name">{{ item.name }}</div>
              <div class="res-region" v-if="item.region">{{ item.region }}</div>
              <div class="res-addr">{{ item.address }}</div>
              <div class="res-actions">
                <button class="btn btn-view btn-sm" @click.stop="openBaiduMap(item)">百度地图</button>
                <button class="btn btn-view btn-sm" @click.stop="openGaodeMap(item)">高德地图</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <baidu-map
        class="bmap"
        :center="mapCenter"
        @ready="mapHandler"
        @click="onMapClick"
        v-bind="mapOptions">
        <bm-navigation anchor="BMAP_ANCHOR_BOTTOM_RIGHT" />
        <!-- 系统门店 -->
        <bm-marker
          v-if="hasMarketLocation"
          :position="marketLocation"
          :icon="marketIcon"
          title="系统门店"
          @click="onStoreCardClick">
          <bm-label
            :content="getMarkerCapHtml('系统门店', 'sys')"
            :labelStyle="markerLabelWrapperStyle"
            :offset="markerCapOffset" />
        </bm-marker>
        <!-- 用户位置 -->
        <bm-marker
          :position="userLocation"
          :icon="userIcon"
          title="用户位置"
          @click="useLocation(userLocationSource, 'user')">
          <bm-label
            :content="getMarkerCapHtml('用户位置', 'user')"
            :labelStyle="markerLabelWrapperStyle"
            :offset="markerCapOffset" />
        </bm-marker>
        <!-- 智能推荐 -->
        <bm-marker
          v-if="recommendation"
          :position="{ lng: recommendation.lng, lat: recommendation.lat }"
          :icon="recIcon"
          title="推荐门店"
          @click="onRecommendationCardClick">
          <bm-label
            :content="getMarkerCapHtml('推荐门店', 'rec')"
            :labelStyle="markerLabelWrapperStyle"
            :offset="markerCapOffset" />
        </bm-marker>
        <!-- 选中位置 -->
        <bm-marker
          v-if="selectedLocation"
          :position="selectedLocation"
          :icon="selectedIcon"
          title="选中位置"
          @click="onSelectedCardClick">
          <bm-label
            :content="getSelectedLabelHtml()"
            :labelStyle="markerLabelWrapperStyle"
            :offset="selectedLabelOffset" />
        </bm-marker>
        <!-- 搜索结果 -->
        <bm-marker
          v-for="item in searchResults"
          :key="'sr-' + item.uid"
          :position="{ lng: item.lng, lat: item.lat }"
          :icon="searchIcon"
          :title="item.name"
          @click="useLocation(item, 'search')">
          <bm-label
            :content="getSearchNumHtml(item.index)"
            :labelStyle="markerLabelWrapperStyle"
            :offset="{ width: -11, height: -11 }" />
        </bm-marker>
      </baidu-map>

      <!-- 右上面板 -->
      <div class="panel-wrap">
        <button class="collapse-btn" title="收起 / 展开" @click="panelCollapsed = !panelCollapsed">
          <i :class="panelCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
        </button>
        <div class="panel" :class="{ collapsed: panelCollapsed }">
          <!-- 1. 系统门店 -->
          <div
            class="sub"
            :class="{ 'is-active': activeCard === 'store' }"
            @click="onStoreCardClick">
            <div class="sub-head">
              <span class="sub-dot" style="background: var(--sys)"></span>
              <span class="sub-title">系统门店</span>
              <span class="sub-id">
                #{{ marketId }}
                <button
                  v-if="marketId"
                  class="btn-copy"
                  type="button"
                  title="复制门店ID"
                  @click.stop="copyText(String(marketId))">
                  <i class="el-icon-document-copy"></i>
                </button>
              </span>
              <button
                class="btn btn-view btn-sm store-photo-btn"
                type="button"
                :disabled="!storePhotos.length"
                @click.stop="openStorePhotos">
                查看门头照
              </button>
              <span v-if="storeUpdated" class="sub-badge badge-ok">已更新</span>
            </div>
            <div class="sub-body" v-show="!panelCollapsed">
              <div class="field">
                <span class="k">门店名称</span>
                <span class="v field-with-copy">
                  <span>{{ marketDetail.marketname || '—' }}</span>
                  <button
                    v-if="marketDetail.marketname"
                    class="btn-copy"
                    type="button"
                    title="复制"
                    @click.stop="copyText(marketDetail.marketname)">
                    <i class="el-icon-document-copy"></i>
                  </button>
                </span>
              </div>
              <div class="field">
                <span class="k">地址</span>
                <span class="v field-with-copy">
                  <span>{{ storeFullAddress || '—' }}</span>
                  <button
                    v-if="storeFullAddress"
                    class="btn-copy"
                    type="button"
                    title="复制"
                    @click.stop="copyText(storeFullAddress)">
                    <i class="el-icon-document-copy"></i>
                  </button>
                </span>
              </div>
              <div class="field">
                <span class="k">经纬度</span>
                <span class="v">{{ formatLngLat(marketLocation) }}</span>
              </div>
            </div>
          </div>

          <!-- 2. 用户位置 -->
          <div
            class="sub"
            :class="{ 'is-active': activeCard === 'user' }"
            @click="useLocation(userLocationSource, 'user')">
            <div class="sub-head">
              <span class="sub-dot" style="background: var(--user)"></span>
              <span class="sub-title">用户位置</span>
            </div>
            <div class="sub-body" v-show="!panelCollapsed">
              <div class="field"><span class="k">门店名称</span><span class="v">{{ marketDetail.marketname || '—' }}</span></div>
              <div class="field"><span class="k">地址</span><span class="v">{{ userFullAddress || '—' }}</span></div>
              <div class="field"><span class="k">经纬度</span><span class="v">{{ formatLngLat(userLocation) }}</span></div>
              <div v-if="actionCard === 'user'" class="btn-row">
                <button class="btn btn-view btn-sm" @click.stop="onCancelSelection">取消</button>
                <button
                  class="btn btn-confirm btn-sm"
                  :disabled="!selectedLocation || saving"
                  @click.stop="openConfirmModal">
                  {{ saving ? '提交中' : '确认更新' }}
                </button>
              </div>
            </div>
          </div>

          <!-- 3. 智能推荐 -->
          <div
            class="sub"
            :class="{ 'is-active': activeCard === 'rec' }"
            @click="onRecommendationCardClick">
            <div class="sub-head">
              <span class="sub-dot" style="background: var(--rec)"></span>
              <span class="sub-title">智能推荐</span>
              <span
                v-if="recommendation"
                class="sub-badge"
                :class="recommendation.isTrusted ? 'badge-ok' : 'badge-bad'">
                {{ recommendation.isTrusted ? '可信度较高' : '可信度不高' }}
              </span>
              <button
                class="btn-refresh"
                :disabled="cozeLoading"
                title="重新校验"
                @click.stop="onCozeValidate">
                <i :class="cozeLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
              </button>
            </div>
            <div class="sub-body" v-show="!panelCollapsed">
              <div v-if="cozeLoading" class="empty"><i class="el-icon-loading"></i> 校验中...</div>
              <template v-else-if="recommendation">
                <div class="field"><span class="k">门店名称</span><span class="v">{{ recommendation.name }}</span></div>
                <div class="field"><span class="k">地址</span><span class="v">{{ recommendation.fullAddress || '—' }}</span></div>
                <div class="field"><span class="k">经纬度</span><span class="v">{{ formatLngLat(recommendation) }}</span></div>
                <div class="field-divider"></div>
                <div class="field">
                  <span class="k">置信度</span>
                  <span class="score-wrap">
                    <span class="v" :class="recommendation.scoreOk ? 'val-ok' : 'val-bad'">{{ recommendation.score }}分</span>
                    <span class="score-tip">（满分10分）</span>
                  </span>
                </div>
                <div class="field">
                  <span class="k">距离系统门店</span>
                  <span class="v" :class="recommendation.distanceOk ? 'val-ok' : 'val-bad'">{{ recommendation.distance }}米</span>
                </div>
                <div class="field" v-if="recommendation.remark"><span class="k">备注</span><span class="v">{{ recommendation.remark }}</span></div>
                <div v-if="actionCard === 'rec'" class="btn-row">
                  <button class="btn btn-view btn-sm" @click.stop="onCancelSelection">取消</button>
                  <button
                    class="btn btn-confirm btn-sm"
                    :disabled="!selectedLocation || saving"
                    @click.stop="openConfirmModal">
                    {{ saving ? '提交中' : '确认更新' }}
                  </button>
                </div>
              </template>
              <div v-else class="empty">暂无智能推荐，请选用用户位置或搜索新地点</div>
            </div>
          </div>

          <!-- 4. 手动查找 -->
          <div
            class="sub"
            :class="{ 'is-active': activeCard === 'selected' }"
            @click="onSelectedCardClick">
            <div class="sub-head">
              <span class="sub-dot" style="background: var(--sel)"></span>
              <span class="sub-title">手动查找</span>
            </div>
            <div class="sub-body" v-show="!panelCollapsed">
              <div class="field">
                <span class="k">经纬度</span>
                <span class="v sel">{{ selectedLocation ? formatLngLat(selectedLocation) : '—' }}</span>
              </div>
              <div class="field"><span class="k">地址</span><span class="v">{{ selectedFullAddress || '—' }}</span></div>
              <div v-if="actionCard === 'selected'" class="btn-row">
                <button class="btn btn-view btn-sm" @click.stop="onCancelSelection">取消</button>
                <button
                  class="btn btn-confirm btn-sm"
                  :disabled="!selectedLocation || saving"
                  @click.stop="openConfirmModal">
                  {{ saving ? '提交中' : '确认更新' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 确认弹窗 -->
      <div class="modal-mask" :class="{ show: confirmVisible }">
        <div class="modal-box">
          <div class="modal-title">确认更新门店经纬度</div>
          <div class="modal-body">
            <div class="modal-row"><span class="mk">门店ID</span><span class="mv">{{ marketId }}</span></div>
            <div class="modal-row"><span class="mk">门店名称</span><span class="mv">{{ marketDetail.marketname || '—' }}</span></div>
            <div class="modal-row"><span class="mk">当前坐标</span><span class="mv">{{ formatLngLat(marketLocation) }}</span></div>
            <div class="modal-row"><span class="mk">当前地址</span><span class="mv">{{ storeFullAddress || '—' }}</span></div>
            <div class="modal-divider"></div>
            <div class="modal-row"><span class="mk">更新后坐标</span><span class="mv red-bold">{{ formatLngLat(selectedLocation) }}</span></div>
            <div class="modal-row"><span class="mk">选中点位置</span><span class="mv">{{ selectedFullAddress || '—' }}</span></div>
            <div class="modal-warn">本次仅更新门店经纬度，门店地址文案保持不变。修改坐标可能影响用户签到判定，请确认无误。</div>
          </div>
          <div class="modal-btns">
            <button class="btn btn-view btn-sm" @click="confirmVisible = false">取消</button>
            <button class="btn btn-confirm btn-sm" :disabled="saving" @click="submitUpdate">确认提交</button>
          </div>
        </div>
      </div>

      <!-- 门头照预览（独立 viewer，避免藏在 overflow:hidden 里看不见） -->
      <el-image-viewer
        v-if="photoViewerVisible"
        :url-list="storePhotos"
        :z-index="4000"
        :on-close="closeStorePhotos" />
    </template>
  </div>
</template>

<script>
import BaiduMap from '@/components/BMapGL/map/BMapGL.vue'
import BmMarker from '@/components/BMapGL/overlays/Marker.vue'
import BmLabel from '@/components/BMapGL/overlays/Label.vue'
import BmNavigation from '@/components/BMapGL/controls/Navigation.vue'
import ElImageViewer from 'element-ui/packages/image/src/image-viewer'
import { runMarketLocationWorkflow, isCozeItemTrusted } from '@/services/coze.api'

const MARKER_ICON_SIZE = 42

const MARKER_COLORS = {
  sys: '#EF4444',
  user: '#3B82F6',
  rec: '#10B981',
  sel: '#F59E0B',
  search: '#8B5CF6',
}

function createColorPinIcon (color) {
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42">',
    `<path fill="${color}" d="M21 3C13.268 3 7 9.268 7 17c0 9.75 12.2 20.65 13.05 21.4a1.5 1.5 0 0 0 1.9 0C22.8 37.65 35 26.75 35 17 35 9.268 28.732 3 21 3z"/>`,
    '<circle fill="#fff" cx="21" cy="17" r="6.5"/>',
    '</svg>',
  ].join('')
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    size: { width: MARKER_ICON_SIZE, height: MARKER_ICON_SIZE },
    opts: {
      anchor: { width: MARKER_ICON_SIZE / 2, height: MARKER_ICON_SIZE },
    },
  }
}

// 透明图标：搜索结果只展示序号标签，不显示图钉
function createEmptyMarkerIcon () {
  return {
    url: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    size: { width: 1, height: 1 },
    opts: {
      anchor: { width: 0, height: 0 },
    },
  }
}

export default {
  components: {
    BaiduMap,
    BmMarker,
    BmLabel,
    BmNavigation,
    ElImageViewer,
  },
  data () {
    return {
      paramError: null,
      loading: false,
      saving: false,
      geocoding: false,
      marketId: null,
      marketDetail: {},
      regionNames: {
        province: '',
        city: '',
        district: '',
      },
      userLocation: { lng: null, lat: null },
      userAddress: '',
      marketLocation: { lng: null, lat: null },
      selectedLocation: null,
      selectedAddress: '',
      selectedSource: null,
      searchKeyword: '',
      searching: false,
      searchResults: [],
      previewUid: null,
      activeCard: null,
      cozeLoading: false,
      cozeResult: null,
      cozePoiList: [],
      panelCollapsed: false,
      confirmVisible: false,
      photoViewerVisible: false,
      storeUpdated: false,
      BMapInstance: null,
      mapInstance: null,
      mapCenter: { lng: 121.497204, lat: 31.243453 },
      marketIcon: createColorPinIcon(MARKER_COLORS.sys),
      userIcon: createColorPinIcon(MARKER_COLORS.user),
      recIcon: createColorPinIcon(MARKER_COLORS.rec),
      selectedIcon: createColorPinIcon(MARKER_COLORS.sel),
      searchIcon: createEmptyMarkerIcon(),
      markerCapOffset: { width: -18, height: 48 },
      selectedLabelOffset: { width: -48, height: 48 },
      markerLabelWrapperStyle: {
        border: 'none',
        backgroundColor: 'transparent',
        padding: 0,
        pointerEvents: 'none',
      },
      mapOptions: {
        proxy: '//bmap-proxy.slicejobs.com/_BMapService/',
        scrollWheelZoom: true,
        zoom: 16,
      },
    }
  },
  computed: {
    hasMarketLocation () {
      const { lng, lat } = this.marketLocation
      return lng && lat && Number(lng) !== 0 && Number(lat) !== 0
    },
    regionText () {
      const { province, city, district } = this.regionNames
      return [province, city, district].filter(Boolean).join('')
    },
    userFullAddress () {
      return this.formatFullAddress('', this.userAddress)
    },
    storeFullAddress () {
      return this.formatFullAddress(this.regionText, this.marketDetail.address)
    },
    selectedFullAddress () {
      return (this.selectedAddress || '').trim()
    },
    storePhotos () {
      const photo = this.marketDetail && this.marketDetail.photos
      if (!photo) {
        return []
      }
      let list = photo
      if (typeof photo === 'string') {
        try {
          const parsed = JSON.parse(photo)
          list = Array.isArray(parsed) ? parsed : (photo ? [photo] : [])
        } catch (e) {
          list = photo ? [photo] : []
        }
      }
      if (!Array.isArray(list)) {
        return []
      }
      return list.map((item) => {
        if (!item) {
          return ''
        }
        if (typeof item === 'string') {
          return item
        }
        return item.url || item.src || item.path || ''
      }).filter(Boolean)
    },
    // 取消 / 确认更新 跟随当前高亮卡片（有选中位置时）
    actionCard () {
      if (!this.selectedLocation) {
        return null
      }
      const hostable = ['user', 'rec', 'selected']
      if (hostable.indexOf(this.activeCard) >= 0) {
        return this.activeCard
      }
      if (this.selectedSource === 'user' || this.selectedSource === 'rec') {
        return this.selectedSource
      }
      // 搜索结果 / 地图点选 → 手动查找
      return 'selected'
    },
    userLocationSource () {
      return {
        lng: this.userLocation.lng,
        lat: this.userLocation.lat,
        address: this.userFullAddress || this.userAddress || this.regionText,
        region: this.regionText,
        name: '用户位置',
      }
    },
    recommendation () {
      if (!this.cozeResult || !this.cozeResult.baidu_name) {
        return null
      }
      const result = this.cozeResult
      const matchedPoi = this.cozePoiList.find(poi => poi.uid === result.baidu_uid)
      const lng = Number(result.baidu_lng) || (matchedPoi && matchedPoi.lng)
      const lat = Number(result.baidu_lat) || (matchedPoi && matchedPoi.lat)
      if (!lng || !lat) {
        return null
      }
      const region = [result.baidu_province, result.baidu_city, result.baidu_district]
        .filter(Boolean)
        .join('')
      const address = result.baidu_address || (matchedPoi && matchedPoi.address) || ''
      const distanceRaw = result.baidu_distance || (matchedPoi && matchedPoi.distance)
      return {
        uid: result.baidu_uid,
        name: result.baidu_name,
        address,
        region,
        fullAddress: this.formatFullAddress(region, address),
        lng,
        lat,
        score: result.vali_score_name || '—',
        distance: distanceRaw || '—',
        remark: result.remark || '',
        scoreOk: Number(result.vali_score_name) >= 7,
        distanceOk: Number(distanceRaw) <= 2000,
        isTrusted: isCozeItemTrusted(result.vali_score_name, distanceRaw),
      }
    },
  },
  created () {
    this.parseQueryParams()
    if (!this.paramError) {
      this.loadMarketDetail()
    }
  },
  methods: {
    formatFullAddress (region, address) {
      const r = (region || '').trim()
      const a = (address || '').trim()
      if (!r && !a) {
        return ''
      }
      if (!r) {
        return a
      }
      if (!a) {
        return r
      }
      if (a.indexOf(r) === 0 || a.indexOf(r) >= 0) {
        return a
      }
      return r + a
    },
    copyText (text) {
      const value = (text || '').trim()
      if (!value) {
        return
      }
      const onSuccess = () => {
        this.$message.success('已复制')
      }
      const onFail = () => {
        this.$message.error('复制失败')
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(onSuccess).catch(onFail)
        return
      }
      const input = document.createElement('textarea')
      input.value = value
      input.setAttribute('readonly', 'readonly')
      input.style.position = 'fixed'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        onSuccess()
      } catch (e) {
        onFail()
      }
      document.body.removeChild(input)
    },
    getMarkerCapHtml (text, type) {
      const color = MARKER_COLORS[type] || '#374151'
      return `<div style="background:#fff;border:1px solid ${color};border-radius:6px;padding:5px 11px;font-size:12.5px;font-weight:700;color:${color};white-space:nowrap;box-shadow:0 2px 8px rgba(17,24,39,.10);">${text}</div>`
    },
    getSelectedLabelHtml () {
      if (!this.selectedLocation) {
        return ''
      }
      const text = this.formatLngLat(this.selectedLocation)
      const color = MARKER_COLORS.sel
      return `<div style="background:#fff;border:1px solid ${color};border-radius:6px;padding:5px 11px;font-size:12.5px;font-weight:700;color:${color};white-space:nowrap;box-shadow:0 2px 8px rgba(17,24,39,.10);">${text}</div>`
    },
    getSearchNumHtml (index) {
      const color = MARKER_COLORS.search
      return `<div style="width:22px;height:22px;border-radius:50%;background:${color};color:#fff;font-size:12px;font-weight:700;line-height:22px;text-align:center;box-shadow:0 2px 6px rgba(139,92,246,.4);">${index}</div>`
    },
    formatLngLat (point) {
      if (!point || point.lng == null || point.lat == null || Number(point.lng) === 0) {
        return '—'
      }
      return `${Number(point.lat).toFixed(5)}, ${Number(point.lng).toFixed(5)}`
    },
    parseQueryParams () {
      const { id, latitude, longitude } = this.$route.query
      const marketId = Number(id)
      const lat = Number(latitude)
      const lng = Number(longitude)

      if (!id || !marketId) {
        this.paramError = '缺少有效的门店 ID 参数（id）'
        return
      }
      if (!latitude || !longitude || Number.isNaN(lat) || Number.isNaN(lng)) {
        this.paramError = '缺少有效的经纬度参数（latitude、longitude）'
        return
      }
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        this.paramError = '经纬度参数超出有效范围'
        return
      }

      this.marketId = marketId
      this.userLocation = { lng, lat }
      this.mapCenter = { lng, lat }
    },
    loadMarketDetail () {
      this.loading = true
      request.page('getMarketDetail', { id: this.marketId }).then(({ data }) => {
        const detail = data || {}
        this.marketDetail = detail
        const lng = Number(detail.longitude)
        const lat = Number(detail.latitude)
        if (lng && lat && lng !== 0 && lat !== 0) {
          this.marketLocation = { lng, lat }
        }
        this.updateMapCenter()
        this.reverseGeocodePoint(this.userLocation, (address) => {
          this.userAddress = address
        })
        // 省市区 / 智能校验失败不应冒充「加载门店信息失败」
        this.loadRegionNames(detail.provinceid, detail.cityid, detail.areaid).finally(() => {
          this.onCozeValidate()
        })
      }).catch((error) => {
        console.error('[MarketLocation] getMarketDetail failed', error)
        this.$message.error('加载门店信息失败')
      }).finally(() => {
        this.loading = false
      })
    },
    findRegionName (list, id) {
      if (!Array.isArray(list) || !id) {
        return ''
      }
      const target = String(id)
      const item = list.find(row => String(row.id) === target)
      return item ? item.name : ''
    },
    loadRegionNames (provinceid, cityid, areaid) {
      this.regionNames = { province: '', city: '', district: '' }
      if (!provinceid) {
        return Promise.resolve()
      }
      return request.alert('getProvinces').then(({ data }) => {
        this.regionNames.province = this.findRegionName(data, provinceid)
        if (!cityid) {
          return
        }
        return request.alert('getCities', { pid: provinceid }).then(({ data: cities }) => {
          this.regionNames.city = this.findRegionName(cities, cityid)
          if (!areaid) {
            return
          }
          return request.alert('getDistricts', { pid: cityid }).then(({ data: districts }) => {
            this.regionNames.district = this.findRegionName(districts, areaid)
          })
        })
      }).catch((error) => {
        console.error('[MarketLocation] loadRegionNames failed', error)
        this.$message.warning('加载省市区名称失败')
      })
    },
    updateMapCenter () {
      if (this.hasMarketLocation) {
        this.mapCenter = { ...this.marketLocation }
      } else {
        this.mapCenter = { ...this.userLocation }
      }
      this.centerMapIfReady()
    },
    centerMapIfReady () {
      const { BMapInstance, mapInstance, mapCenter, mapOptions } = this
      if (!BMapInstance || !mapInstance || !mapCenter) {
        return
      }
      if (mapCenter.lng == null || mapCenter.lat == null) {
        return
      }
      const point = new BMapInstance.Point(mapCenter.lng, mapCenter.lat)
      const zoom = (mapInstance.getZoom && mapInstance.getZoom()) || mapOptions.zoom || 16
      mapInstance.centerAndZoom(point, zoom)
    },
    mapHandler ({ BMap, map }) {
      this.BMapInstance = BMap
      this.mapInstance = map
      this.centerMapIfReady()
    },
    onMapClick (e) {
      if (!e.overlay) {
        this.useLocation({
          lng: e.point.lng,
          lat: e.point.lat,
          address: '',
          name: '地图选点',
        }, 'map', { moveMap: false })
      }
    },
    focusMarker (type) {
      let point = null
      if (type === 'user') {
        point = this.userLocation
      } else if (type === 'store') {
        point = this.hasMarketLocation ? this.marketLocation : null
      } else if (type === 'rec' && this.recommendation) {
        point = { lng: this.recommendation.lng, lat: this.recommendation.lat }
      } else if (type === 'selected' && this.selectedLocation) {
        point = this.selectedLocation
      }
      if (!point || point.lng == null || point.lat == null) {
        return
      }
      this.mapCenter = { lng: point.lng, lat: point.lat }
      this.centerMapIfReady()
    },
    onStoreCardClick () {
      this.activeCard = 'store'
      this.focusMarker('store')
    },
    openStorePhotos () {
      if (!this.storePhotos.length) {
        this.$message.warning('暂无门头照')
        return
      }
      this.photoViewerVisible = true
    },
    closeStorePhotos () {
      this.photoViewerVisible = false
    },
    onSelectedCardClick () {
      this.activeCard = 'selected'
      this.focusMarker('selected')
    },
    useLocation (item, source, options = {}) {
      if (!item || !item.lng || !item.lat) {
        return
      }
      const moveMap = options.moveMap !== false
      this.selectedLocation = { lng: item.lng, lat: item.lat }
      this.selectedSource = source
      const region = item.region || this.regionText
      const detailAddress = (item.fullAddress || item.address || '').trim()
      // 仅有省市区、没有详细地址时先留空，走逆地理补全，避免只显示省市区
      if (detailAddress && detailAddress !== (region || '').trim()) {
        this.selectedAddress = item.fullAddress || this.formatFullAddress(region, item.address)
      } else if (detailAddress && !region) {
        this.selectedAddress = detailAddress
      } else {
        this.selectedAddress = ''
      }
      if (source === 'search' || source === 'map') {
        this.previewUid = source === 'search' ? item.uid : this.previewUid
        this.activeCard = 'selected'
      } else {
        this.activeCard = source
      }
      if (moveMap) {
        this.mapCenter = { lng: item.lng, lat: item.lat }
        this.centerMapIfReady()
      }
      if (!this.selectedAddress) {
        this.reverseGeocodePoint(this.selectedLocation, (address) => {
          // 百度逆地理通常已含省市区 + 详细地址
          this.selectedAddress = (address || '').trim() || this.formatFullAddress(region, address)
        })
      }
    },
    onRecommendationCardClick () {
      if (!this.recommendation) {
        this.activeCard = 'rec'
        this.focusMarker('rec')
        return
      }
      this.useLocation(this.recommendation, 'rec')
    },
    previewSearchItem (item) {
      this.previewUid = item.uid
      this.mapCenter = { lng: item.lng, lat: item.lat }
      this.centerMapIfReady()
    },
    clearSearch () {
      this.searchKeyword = ''
      this.searchResults = []
      this.previewUid = null
      this.searching = false
    },
    openBaiduMap (item) {
      if (!item) {
        return
      }
      if (item.detailUrl) {
        window.open(item.detailUrl, '_blank')
        return
      }
      const keyword = item.name || ''
      if (!keyword) {
        return
      }
      const region = this.regionNames.city ||
        this.regionNames.province ||
        this.marketDetail.cityname ||
        this.marketDetail.provincename ||
        '全国'
      const url = `https://api.map.baidu.com/place/search?query=${encodeURIComponent(keyword)}&region=${encodeURIComponent(region)}&output=html`
      window.open(url, '_blank')
    },
    openGaodeMap (item) {
      const keyword = (item && item.name) || ''
      if (!keyword) {
        return
      }
      const url = `https://uri.amap.com/search?keyword=${encodeURIComponent(keyword)}&view=map`
      window.open(url, '_blank')
    },
    searchAddress () {
      const keyword = this.searchKeyword.trim()
      if (!keyword) {
        this.$message.warning('请输入地址')
        return
      }
      if (!this.BMapInstance || !this.mapInstance) {
        this.$message.warning('地图尚未加载完成')
        return
      }
      this.searching = true
      this.searchResults = []
      this.previewUid = null
      const localSearch = new this.BMapInstance.LocalSearch(this.mapInstance, {
        onSearchComplete: (results) => {
          this.searching = false
          const status = localSearch.getStatus()
          const ok = status === 0 || status === window.BMAP_STATUS_SUCCESS
          if (!results || !ok) {
            this.$message.warning('未找到该地址，请换个关键词试试')
            return
          }
          const list = []
          const count = results.getCurrentNumPois()
          for (let i = 0; i < count; i++) {
            const poi = results.getPoi(i)
            list.push({
              index: i + 1,
              uid: poi.uid || `search-${i}`,
              name: poi.title,
              address: poi.address,
              region: poi.city || '',
              lng: poi.point.lng,
              lat: poi.point.lat,
              detailUrl: poi.detailUrl || poi.url || '',
            })
          }
          this.searchResults = list
          if (list.length) {
            this.mapCenter = { lng: list[0].lng, lat: list[0].lat }
            this.centerMapIfReady()
            this.$message.success(`找到 ${list.length} 条结果，已标注在地图上`)
          } else {
            this.$message.warning('未找到该地址，请换个关键词试试')
          }
        },
      })
      localSearch.search(keyword)
    },
    reverseGeocodePoint (point, callback) {
      if (!this.BMapInstance || !point || !point.lng) {
        return
      }
      const geocoder = new this.BMapInstance.Geocoder()
      const bPoint = new this.BMapInstance.Point(point.lng, point.lat)
      geocoder.getLocation(bPoint, (result) => {
        callback(result ? result.address : '无法解析地址')
      })
    },
    buildCozePayload () {
      const detail = this.marketDetail
      return {
        shop_id: String(this.marketId),
        user_id: String(this.$route.query.user_id || ''),
        original_name: detail.marketname || '',
        original_province: this.regionNames.province || detail.provincename || '',
        original_city: this.regionNames.city || detail.cityname || '',
        original_district: this.regionNames.district || detail.areaname || '',
        original_town: detail.street || '',
        original_address: detail.address || '',
        lon_baidu: String(detail.longitude || this.marketLocation.lng || ''),
        lat_baidu: String(detail.latitude || this.marketLocation.lat || ''),
        lon_gaode: String(detail.lon_gd || ''),
        lat_gaode: String(detail.lat_gd || ''),
        lon_user: String(this.userLocation.lng || ''),
        lat_user: String(this.userLocation.lat || ''),
      }
    },
    onCozeValidate () {
      this.cozeLoading = true
      this.cozeResult = null
      this.cozePoiList = []
      runMarketLocationWorkflow(this.buildCozePayload()).then(({ raw, poiList }) => {
        this.cozeResult = raw
        this.cozePoiList = poiList
        this.$message.success('智能校验完成')
      }).catch((error) => {
        this.$message.error(error.message || '智能校验失败')
      }).finally(() => {
        this.cozeLoading = false
      })
    },
    onCancelSelection () {
      this.selectedLocation = null
      this.selectedAddress = ''
      this.selectedSource = null
      this.activeCard = null
    },
    openConfirmModal () {
      if (!this.selectedLocation) {
        this.$message.warning('请先选用一个位置')
        return
      }
      this.confirmVisible = true
    },
    submitUpdate () {
      if (!this.selectedLocation) {
        return
      }
      this.saving = true
      const datasourceMap = {
        user: '用户位置',
        rec: '智能推荐',
      }
      const recommendation = this.recommendation
      const payload = {
        id: this.marketId,
        longitude: this.selectedLocation.lng,
        latitude: this.selectedLocation.lat,
        map_type: 0,
        marketname: this.marketDetail.marketname,
        address: this.marketDetail.address,
        provinceid: this.marketDetail.provinceid,
        cityid: this.marketDetail.cityid,
        areaid: this.marketDetail.areaid,
        cs_location_modify_data: {
          datasource: datasourceMap[this.selectedSource] || '手动查找',
          userlongitude: this.userLocation.lng,
          userlatitude: this.userLocation.lat,
          reclongitude: recommendation ? recommendation.lng : null,
          reclatitude: recommendation ? recommendation.lat : null,
          recmarketname: recommendation ? recommendation.name : '',
          recprovincecityarea: recommendation ? recommendation.region : '',
          recaddress: recommendation ? recommendation.address : '',
          recscore: recommendation ? recommendation.score : '',
          recdistance: recommendation ? recommendation.distance : '',
        },
      }
      request.common('updateMarketData', payload).then(() => {
        this.$message.success('门店经纬度修改成功')
        this.marketLocation = { ...this.selectedLocation }
        this.marketDetail.longitude = this.selectedLocation.lng
        this.marketDetail.latitude = this.selectedLocation.lat
        this.storeUpdated = true
        this.confirmVisible = false
        this.onCancelSelection()
      }).finally(() => {
        this.saving = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.market-location-page {
  --sys: #EF4444;
  --user: #3B82F6;
  --rec: #10B981;
  --sel: #F59E0B;
  --search: #8B5CF6;
  --ink: #111827;
  --ink-2: #374151;
  --ink-3: #6B7280;
  --line: #E5E7EB;
  --shadow: 0 6px 24px rgba(17, 24, 39, .12);
  --shadow-sm: 0 2px 8px rgba(17, 24, 39, .10);

  position: relative;
  width: 100%;
  height: calc(100vh - 84px);
  min-height: 500px;
  overflow: hidden;
  background: #EAF1F7;
  color: var(--ink);
}

.param-error {
  padding: 24px;
}

.loading-indicator {
  position: absolute;
  top: 68px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  font-size: 13px;
  color: var(--ink-2);
}

.bmap {
  width: 100%;
  height: 100%;
}

/* ===== 搜索 ===== */
.search-wrap {
  position: absolute;
  left: 14px;
  top: 14px;
  width: 330px;
  max-width: calc(100% - 28px);
  z-index: 30;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow);
  padding: 0 6px 0 12px;
}

.search-icon {
  color: #9CA3AF;
  font-size: 16px;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: var(--ink);
  background: transparent;
  min-width: 0;
}

.search-clear {
  flex: none;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #9CA3AF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 14px;

  &:hover {
    color: #6B7280;
  }
}

.search-btn {
  border: none;
  background: var(--user);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.search-results {
  margin-top: 8px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow);
  overflow: hidden;
  max-height: 360px;
  overflow-y: auto;
}

.res-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &.is-active {
    background: #F8FAFC;
  }
}

.res-idx {
  flex: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--search);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}

.res-main {
  flex: 1;
  min-width: 0;
}

.res-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
}

.res-region,
.res-addr {
  font-size: 11px;
  color: var(--ink-2);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.res-region {
  color: var(--ink-3);
  font-size: 10px;
}

.res-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

/* ===== 右上面板 ===== */
.panel-wrap {
  position: absolute;
  right: 64px;
  top: 14px;
  z-index: 30;
  display: flex;
  align-items: flex-start;
}

.collapse-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  margin-top: 6px;
  color: #9CA3AF;
  font-size: 14px;
}

.panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  background: rgba(255, 255, 255, .98);
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow: var(--shadow);
  padding: 10px;
  width: 280px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;

  &.collapsed .sub-body {
    display: none;
  }

  &.collapsed .sub-head {
    border-bottom: none;
  }
}

.sub {
  flex: none;
  width: 100%;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color .15s ease, box-shadow .15s ease;

  &.is-active {
    border-color: #3B82F6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, .18);
  }
}

.sub-head {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 9px;
  background: #FCFDFE;
  border-bottom: 1px solid var(--line);
  user-select: none;
}

.sub-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}

.sub-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--ink);
}

.sub-id {
  font-size: 10px;
  font-weight: 600;
  color: var(--ink-3);
  margin-left: 2px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.store-photo-btn {
  margin-left: auto;
}

.sub-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: 4px;
}

.badge-rec {
  background: #D1FAE5;
  color: #047857;
}

.badge-warn {
  background: #FEF3C7;
  color: #B45309;
}

.badge-ok {
  background: #D1FAE5;
  color: #047857;
}

.badge-bad {
  background: #FEE2E2;
  color: #DC2626;
}

.btn-refresh {
  border: none;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
  padding: 0 2px;
  font-size: 13px;
  margin-left: 2px;

  &:disabled {
    cursor: not-allowed;
  }
}

.sub-body {
  padding: 8px 9px 9px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.field {
  display: flex;
  gap: 5px;
  font-size: 10px;
  line-height: 1.45;
  margin-bottom: 3px;
}

.field .k {
  color: var(--ink-3);
  flex: none;
  width: 50px;
}

.field .v {
  color: var(--ink-2);
  word-break: break-all;
}

.field-with-copy {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.btn-copy {
  flex: none;
  border: none;
  background: transparent;
  color: #9CA3AF;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  line-height: 1.45;

  &:hover {
    color: #3B82F6;
  }
}

.field .v.warn {
  color: var(--sys);
  font-weight: 600;
}

.field .v.ok {
  color: var(--rec);
  font-weight: 600;
}

.field .v.sel,
.update-line .v.sel {
  color: var(--sel);
  font-weight: 700;
}

.red-bold {
  color: #EF4444 !important;
  font-weight: 700;
}

.val-ok {
  color: #059669 !important;
  font-weight: 700;
}

.val-bad {
  color: #EF4444 !important;
  font-weight: 700;
}

.score-wrap {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.score-tip {
  font-size: 10.5px;
  color: #A1A1AA;
  font-weight: 400;
}

.field-divider {
  border-top: 1px dashed var(--line);
  margin: 4px 0 6px;
}

.update-line {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed var(--line);
}

.update-line .k {
  font-size: 10px;
  color: var(--ink-3);
  width: 50px;
  flex: none;
}

.update-line .v {
  font-size: 10px;
  font-variant-numeric: tabular-nums;
}

.empty {
  font-size: 11px;
  color: var(--ink-3);
  line-height: 1.5;
  padding: 4px 0;
}

.btn-row {
  display: flex;
  gap: 5px;
  margin-top: auto;
  padding-top: 8px;
}

.btn-row .btn {
  flex: 1;
}

.btn {
  font-family: inherit;
  height: 26px;
  border: none;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.btn-sm {
  height: 22px;
  font-size: 10px;
  padding: 0 8px;
  border-radius: 5px;
}

.btn-use {
  background: var(--sel);
  color: #fff;
  box-shadow: 0 2px 5px rgba(245, 158, 11, .35);
}

.btn-view {
  background: #fff;
  color: var(--ink-2);
  border: 1px solid var(--line);
}

.btn-confirm {
  background: #2563EB;
  color: #fff;
  box-shadow: 0 2px 5px rgba(37, 99, 235, .35);
}

.btn:disabled {
  background: #E5E7EB;
  color: #9CA3AF;
  cursor: not-allowed;
  box-shadow: none;
  border-color: #E5E7EB;
}

/* ===== 确认弹窗 ===== */
.modal-mask {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, .45);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 60;

  &.show {
    display: flex;
  }
}

.modal-box {
  width: 320px;
  max-width: 90vw;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, .28);
  overflow: hidden;
  padding: 0;
}

.modal-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  padding: 16px 18px 10px;
}

.modal-body {
  padding: 0 18px 6px;
}

.modal-row {
  display: flex;
  gap: 10px;
  font-size: 12px;
  line-height: 1.7;
}

.modal-row .mk {
  color: var(--ink-3);
  flex: none;
  width: 72px;
  white-space: nowrap;
}

.modal-row .mv {
  color: var(--ink);
  word-break: break-all;
}

.modal-divider {
  border-top: 1px dashed var(--line);
  margin: 8px 0;
}

.modal-warn {
  margin-top: 8px;
  font-size: 11px;
  color: #B45309;
  background: #FEF3C7;
  border-radius: 8px;
  padding: 8px 10px;
  line-height: 1.5;
}

.modal-btns {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 18px 16px;
}
</style>



// WEBPACK FOOTER //
// src/views/market/pages/MarketLocation.vue